"use client";

import { useCallback, useState } from "react";
import {
  Button,
  ModalDialogBody,
  ModalDialogFooter,
  ModalDialogHeader,
  ModalDialogRoot,
  Spacer,
  Text,
} from "@zapier/design-system";
import { useTranslations } from "@zapier/i18n";
import {
  getMessageFromNetworkError,
  useBulkDeleteAssets,
  useBulkRestoreAssets,
  useBulkTrashAssets,
  useFavoriteAssets,
} from "@zapier/client";
import { AssetTypes, FOLDER_TRASH_ID, useNotification } from "@zapier/utils";
import { FlexSpacer } from "../../common/FlexSpacer/FlexSpacer";
import { useBulkActionLimitGuard } from "../../../hooks/useBulkActionLimitGuard";
import { TrashNotification } from "../../common/TrashNotification/TrashNotification";

interface TypedAssetId {
  assetType: string;
  assetId: string;
}

// Asset ids here are typed (e.g. "canvas:abc123"), matching convertToAssetId's
// "<assetType>:<assetId>" format.
function parseTypedAssetId(typedAssetId: string): TypedAssetId | undefined {
  const separatorIndex = typedAssetId.indexOf(":");
  if (separatorIndex === -1) {
    return undefined;
  }
  return {
    assetType: typedAssetId.slice(0, separatorIndex),
    assetId: typedAssetId.slice(separatorIndex + 1),
  };
}

function isTypedAssetId(
  parsed: TypedAssetId | undefined,
): parsed is TypedAssetId {
  return parsed !== undefined;
}

// The toast's noun: the shared type ("zaps"/"canvases"), or "items" for a mixed selection.
function getAssetTypeLabel(
  typedAssetIds: string[],
): "canvas" | "zap" | "other" {
  const assetTypes = new Set(
    typedAssetIds.map(
      (typedAssetId) => parseTypedAssetId(typedAssetId)?.assetType,
    ),
  );
  if (assetTypes.size !== 1) {
    return "other";
  }
  const [assetType] = assetTypes;
  if (assetType === AssetTypes.Canvas) {
    return "canvas";
  }
  if (assetType === AssetTypes.Zap) {
    return "zap";
  }
  return "other";
}

interface AssetDeleteModalProps {
  selectedAssetIds: string[];
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  folderId?: string;
  onSettled?: () => void;
  // When a single asset is selected, its name lets the move-to-trash title read
  // "Move “Name” to trash?" instead of "Move 1 item to trash?".
  singleAssetName?: string;
}

export function AssetDeleteModal({
  selectedAssetIds,
  isOpen,
  setIsOpen,
  folderId,
  onSettled,
  singleAssetName,
}: AssetDeleteModalProps) {
  const t = useTranslations("bulk-delete-modal");
  const { createNotification } = useNotification();
  const notifyIfOverBulkLimit = useBulkActionLimitGuard("delete");
  const isTrash = folderId === FOLDER_TRASH_ID;
  const bulkTrashAssets = useBulkTrashAssets({ folderId });
  const bulkDeleteAssets = useBulkDeleteAssets();
  const bulkRestoreAssets = useBulkRestoreAssets({ folderId });
  const { removeManyFromFavorites } = useFavoriteAssets();
  const [isDeleting, setIsDeleting] = useState(false);

  const itemCount = selectedAssetIds.length;
  const translationNamespace = isTrash ? "permanent" : "trash";

  const handleUndoTrash = useCallback(
    async (assetIds: string[]) => {
      try {
        // A resolved mutation is not a restored asset: per-id outcomes come
        // back in the payload, so report those rather than the ids we asked for.
        const { succeededAssetIds, failedAssetIds } =
          await bulkRestoreAssets.mutateAsync({ assetIds });

        if (failedAssetIds.length === 0) {
          const named = succeededAssetIds.length === 1 && !!singleAssetName;
          createNotification({
            message: t("notifications.trash.restore-success", {
              hasName: named ? "true" : "false",
              name: singleAssetName ?? "",
              count: succeededAssetIds.length,
            }),
            type: "success",
          });
        } else if (succeededAssetIds.length === 0) {
          createNotification({
            message: t("notifications.trash.restore-error"),
            type: "error",
          });
        } else {
          createNotification({
            message: t("notifications.trash.restore-partial", {
              succeededCount: succeededAssetIds.length,
              failedCount: failedAssetIds.length,
            }),
            type: "warning",
          });
        }
      } catch {
        createNotification({
          message: t("notifications.trash.restore-error"),
          type: "error",
        });
      }
    },
    [bulkRestoreAssets, createNotification, singleAssetName, t],
  );

  const handleDelete = useCallback(async () => {
    if (notifyIfOverBulkLimit(itemCount)) {
      setIsOpen(false);
      return;
    }

    setIsDeleting(true);

    try {
      const mutation = isTrash ? bulkDeleteAssets : bulkTrashAssets;
      const { succeededAssetIds, failedAssetIds } = await mutation.mutateAsync({
        assetIds: selectedAssetIds,
      });

      // Permanent delete removes favorite membership for the acting user, for
      // every asset that actually succeeded — independent of whether other
      // assets in the same batch failed. Cleaned up in one write (rather than
      // one removeFromFavorites call per asset) since the favorites property
      // is replaced wholesale on every write: sequential single-asset writes
      // would race and could silently restore memberships removed by an
      // earlier, now-overwritten call. This does not clean up other users'
      // favorites for these assets.
      if (isTrash) {
        removeManyFromFavorites(
          succeededAssetIds.map(parseTypedAssetId).filter(isTypedAssetId),
        );
      }

      if (failedAssetIds.length === 0) {
        if (isTrash) {
          createNotification({
            message: t("notifications.permanent.success", {
              count: succeededAssetIds.length,
            }),
            type: "success",
          });
        } else {
          const named = succeededAssetIds.length === 1 && !!singleAssetName;
          createNotification({
            message: (
              <TrashNotification
                message={t("notifications.trash.success", {
                  hasName: named ? "true" : "false",
                  name: singleAssetName ?? "",
                  count: succeededAssetIds.length,
                  assetTypeLabel: getAssetTypeLabel(succeededAssetIds),
                })}
                onUndo={() => handleUndoTrash(succeededAssetIds)}
              />
            ),
            type: "success",
          });
        }
      } else {
        createNotification({
          title: t("errors.title", { count: failedAssetIds.length }),
          message: t(`notifications.${translationNamespace}.partial` as const, {
            succeededCount: succeededAssetIds.length,
            failedCount: failedAssetIds.length,
          }),
          type: "warning",
        });
      }
    } catch (error) {
      createNotification({
        title: t("errors.title", { count: itemCount }),
        message: getMessageFromNetworkError(
          error as Parameters<typeof getMessageFromNetworkError>[0],
        ),
        type: "error",
      });
    } finally {
      setIsDeleting(false);
      setIsOpen(false);
      onSettled?.();
    }
  }, [
    bulkDeleteAssets,
    bulkTrashAssets,
    createNotification,
    handleUndoTrash,
    isTrash,
    itemCount,
    notifyIfOverBulkLimit,
    onSettled,
    removeManyFromFavorites,
    selectedAssetIds,
    setIsOpen,
    t,
    translationNamespace,
  ]);

  if (!isOpen) {
    return;
  }

  return (
    <ModalDialogRoot isOpen={isOpen} onOpenChange={setIsOpen}>
      <>
        <ModalDialogHeader textAlign="start">
          {!isTrash && itemCount === 1 && singleAssetName
            ? t("header.trash.title-named", { name: singleAssetName })
            : t(`header.${translationNamespace}.title` as const, {
                count: itemCount,
              })}
        </ModalDialogHeader>
        <ModalDialogBody textAlign="start">
          <Text color="TextWeaker">
            {t(`body.${translationNamespace}.explanation` as const, {
              count: itemCount,
            })}
          </Text>
          {!isTrash && (
            <>
              <Spacer height={12} />
              <Text color="TextWeaker">{t("body.trash.connected-assets")}</Text>
            </>
          )}
        </ModalDialogBody>
        <ModalDialogFooter>
          <Button
            variant="tertiary"
            isDisabled={isDeleting}
            onPress={() => setIsOpen(false)}
          >
            {t("buttons.cancel")}
          </Button>

          <FlexSpacer />

          <Button
            variant="danger"
            isDisabled={isDeleting}
            isPending={isDeleting}
            onPress={handleDelete}
          >
            {t(`buttons.${translationNamespace}.delete` as const, {
              count: itemCount,
            })}
          </Button>
        </ModalDialogFooter>
      </>
    </ModalDialogRoot>
  );
}
